import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { Avatar } from "@/components/ui/misc";
import { maskQuoted } from "@/components/mention-text";
import { linkPresence, type LinkPresence } from "@/lib/foreign-agents";
import type { AgentWithContext } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * `@` autocomplete for every composer that can address an agent: a card's thread,
 * its description, and agent chats. One implementation, so a tag behaves the same
 * wherever it is typed.
 */

export interface MentionCandidate {
  handle: string;
  name: string;
  /** The project (or "workspace"), shown beside the handle. */
  detail: string;
  kind: AgentWithContext["kind"];
  /** A foreign agent's machine: connected or not (its avatar's mark). */
  presence?: LinkPresence;
  /** Avatar icon and tint keys (`lib/avatars.tsx`). */
  icon: string | null;
  iconColor: string | null;
  /** Lower sorts first when the query matches equally well. */
  rank: number;
  /** Why it is ranked where it is ("assignee", "this project"), shown as a tag. */
  reason?: string;
}

/**
 * Who `@` can reach from here, per core's rules: every agent with a session and
 * the master. `claude` is not offered: it is the System actor, and core closes a
 * person's `@claude` as dismissed at once. People are not offered — a mention of a person opens no request in core, so
 * listing them would promise something that does not happen.
 */
export function mentionCandidates(
  agents: AgentWithContext[],
  context: {
    /** The card's assignee, ranked first. */
    assigneeId?: string | null;
    /** The card's effective project (its own, else its board's), ranked next. */
    projectId?: string | null;
    /**
     * Other projects ranked as the card's: a card naming none, on a board covering
     * several, is about any of them.
     */
    projectIds?: string[];
    /** Leave this agent out (the one whose chat this is). */
    excludeId?: string;
  } = {},
): MentionCandidate[] {
  const out: MentionCandidate[] = [];
  for (const agent of agents) {
    if (agent.id === context.excludeId) continue;
    const assignee = Boolean(context.assigneeId) && agent.id === context.assigneeId;
    const sameProject =
      agent.projectId !== null &&
      (agent.projectId === context.projectId || (context.projectIds ?? []).includes(agent.projectId));
    out.push({
      handle: agent.handle,
      name: agent.name,
      detail:
        agent.project?.name ??
        (agent.kind === "foreign" ? (agent.link?.machine ? `on ${agent.link.machine}` : "own machine") : "workspace"),
      kind: agent.kind,
      ...(agent.kind === "foreign" ? { presence: linkPresence(agent.link) } : {}),
      icon: agent.icon ?? (agent.kind === "master" ? "crown" : agent.kind === "foreign" ? "laptop" : "bot"),
      iconColor: agent.iconColor,
      rank: assignee ? 0 : sameProject ? 1 : agent.kind === "master" ? 3 : 2,
      reason: assignee ? "assignee" : sameProject ? "this project" : undefined,
    });
  }
  return out;
}

/** How well `query` matches: 0 handle prefix, 1 a name word's prefix, 2 anywhere; null none. */
function score(candidate: MentionCandidate, query: string): number | null {
  if (!query) return 0;
  const handle = candidate.handle.toLowerCase();
  const name = candidate.name.toLowerCase();
  if (handle.startsWith(query)) return 0;
  if (name.split(/[\s_-]+/).some((word) => word.startsWith(query))) return 1;
  if (handle.includes(query) || name.includes(query) || candidate.detail.toLowerCase().includes(query)) return 2;
  return null;
}

export function filterCandidates(candidates: MentionCandidate[], query: string): MentionCandidate[] {
  const needle = query.toLowerCase();
  return candidates
    .map((candidate) => ({ candidate, score: score(candidate, needle) }))
    .filter((entry): entry is { candidate: MentionCandidate; score: number } => entry.score !== null)
    .sort((a, b) => a.score - b.score || a.candidate.rank - b.candidate.rank || a.candidate.name.localeCompare(b.candidate.name))
    .map((entry) => entry.candidate);
}

/**
 * The `@token` the caret is in, if any. The leading guard is core's (`[^\w@/]`
 * before the `@`), so the popup only opens where the parser would read a mention —
 * not in an email address, and not inside code or a `>` quote (`maskQuoted`).
 */
export function tokenAt(text: string, caret: number): { start: number; query: string } | null {
  const before = text.slice(0, caret);
  const match = /(^|[^\w@/])@([a-z0-9_-]{0,31})$/i.exec(before);
  if (!match) return null;
  const start = match.index + match[1]!.length;
  if (maskQuoted(text)[start] !== "@") return null;
  return { start, query: match[2]! };
}

/** Inserts `text` over [start, end) so the browser's undo still works, with a fallback. */
function replaceRange(el: HTMLTextAreaElement, start: number, end: number, text: string) {
  el.focus();
  el.setSelectionRange(start, end);
  let done = false;
  try {
    done = document.execCommand("insertText", false, text);
  } catch {
    done = false;
  }
  if (!done) {
    const next = el.value.slice(0, start) + text + el.value.slice(end);
    const setter = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, "value")?.set;
    setter?.call(el, next);
    el.dispatchEvent(new Event("input", { bubbles: true }));
    el.setSelectionRange(start + text.length, start + text.length);
  }
}

/**
 * Wires the popup to a textarea. Returns the key handler to run first in the
 * textarea's `onKeyDown` (it swallows the keys it uses) and the popup to render
 * in a `relative` wrapper around the textarea.
 */
export function useMentionAutocomplete(
  ref: React.RefObject<HTMLTextAreaElement | null>,
  value: unknown,
  candidates: MentionCandidate[] | undefined,
  placement: "above" | "below" = "above",
) {
  const [token, setToken] = useState<{ start: number; query: string } | null>(null);
  const [active, setActive] = useState(0);
  const [focused, setFocused] = useState(false);
  /** The `@` the user pressed Esc on; it stays closed until they move to another. */
  const [dismissed, setDismissed] = useState<number | null>(null);

  const refresh = React.useCallback(() => {
    const el = ref.current;
    if (!el || !candidates) return setToken(null);
    const caret = el.selectionStart === el.selectionEnd ? el.selectionStart : null;
    const next = caret === null ? null : tokenAt(el.value, caret);
    if (!next) setDismissed(null);
    setToken((current) =>
      current?.start === next?.start && current?.query === next?.query ? current : next,
    );
  }, [ref, candidates]);

  // Typing (value changes) and caret moves (select/keyup/click) both re-read the token.
  useEffect(() => {
    refresh();
  }, [value, refresh]);
  useEffect(() => {
    const el = ref.current;
    if (!el || !candidates) return;
    const onFocus = () => {
      setFocused(true);
      refresh();
    };
    // Late, so a tap on an item lands before the popup goes.
    const onBlur = () => window.setTimeout(() => setFocused(false), 150);
    el.addEventListener("select", refresh);
    el.addEventListener("click", refresh);
    el.addEventListener("keyup", refresh);
    el.addEventListener("focus", onFocus);
    el.addEventListener("blur", onBlur);
    if (document.activeElement === el) setFocused(true);
    return () => {
      el.removeEventListener("select", refresh);
      el.removeEventListener("click", refresh);
      el.removeEventListener("keyup", refresh);
      el.removeEventListener("focus", onFocus);
      el.removeEventListener("blur", onBlur);
    };
  }, [ref, candidates, refresh]);

  const matches = useMemo(
    () => (token && candidates ? filterCandidates(candidates, token.query).slice(0, 8) : []),
    [token, candidates],
  );
  const open = focused && token !== null && token.start !== dismissed && matches.length > 0;

  useEffect(() => setActive(0), [token?.start, token?.query]);

  // Esc must close the popup, not the sheet or dialog around it. Radix listens for
  // Escape on the document in the capture phase, so this listens on the window —
  // earlier still — and marks the event handled, which Radix respects.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.target !== ref.current) return;
      event.preventDefault();
      event.stopPropagation();
      setDismissed(token?.start ?? null);
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
  }, [open, ref, token?.start]);

  const pick = (candidate: MentionCandidate) => {
    const el = ref.current;
    if (!el || !token) return;
    const end = token.start + 1 + token.query.length;
    const trailing = el.value[end] === " " ? "" : " ";
    replaceRange(el, token.start, end, `@${candidate.handle}${trailing}`);
    const caret = token.start + candidate.handle.length + 2;
    el.setSelectionRange(caret, caret);
    setToken(null);
  };

  /** Run first in onKeyDown; returns true when it used the key. */
  const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>): boolean => {
    if (!open || event.nativeEvent.isComposing) return false;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActive((current) => (current + step + matches.length) % matches.length);
      return true;
    }
    if ((event.key === "Enter" && !event.ctrlKey && !event.metaKey) || (event.key === "Tab" && !event.shiftKey)) {
      event.preventDefault();
      const chosen = matches[Math.min(active, matches.length - 1)];
      if (chosen) pick(chosen);
      return true;
    }
    return false;
  };

  const popup = open ? (
    <div
      role="listbox"
      aria-label="Mention an agent"
      // Kept in the page rather than portaled: a modal sheet turns pointer events
      // off outside itself, and a portaled list would not take a tap.
      className={cn(
        "absolute left-0 right-0 z-50 max-h-60 overflow-y-auto rounded-md border border-border bg-elevated p-1 shadow-xl scrollbar-slim sm:right-auto sm:w-80",
        placement === "above" ? "bottom-full mb-1.5" : "top-full mt-1.5",
      )}
      onPointerDown={(event) => event.preventDefault()}
      onMouseDown={(event) => event.preventDefault()}
    >
      {matches.map((candidate, index) => {
        return (
          <button
            key={candidate.handle}
            type="button"
            role="option"
            aria-selected={index === active}
            tabIndex={-1}
            onMouseEnter={() => setActive(index)}
            onClick={() => pick(candidate)}
            className={cn(
              "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-[13px]",
              index === active ? "bg-accent text-accent-foreground" : "text-foreground",
            )}
          >
            <Avatar name={candidate.name} icon={candidate.icon} iconColor={candidate.iconColor} size="sm" foreign={candidate.presence} />
            <span className="min-w-0 flex-1">
              <span className="flex items-baseline gap-1.5">
                <span className="truncate font-medium">{candidate.name}</span>
                <span className="shrink-0 text-[11.5px] text-muted-foreground">@{candidate.handle}</span>
              </span>
              <span className="block truncate text-[11px] text-muted-foreground">{candidate.detail}</span>
            </span>
            {candidate.reason ? (
              <span className="shrink-0 rounded-full bg-primary/12 px-1.5 py-px text-[9.5px] font-medium uppercase tracking-wide text-primary">
                {candidate.reason}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  ) : null;

  return { open, onKeyDown, popup };
}
